import { Archive, CalendarClock, Check, Link2, Maximize2, MoreHorizontal, PanelRight, RotateCcw, ShieldCheck, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { Task } from '../../api/types';
import { useShell } from '../../app/shell-context';
import { useSession } from '../../app/session';
import { IconButton } from '../../components/ui/Button';
import { Menu, type MenuItem, useMenu } from '../../components/ui/Menu';
import { useTaskActions } from './TaskActions';

/** Пункти контекстного меню задачі — однакові в рядку, картці дошки й панелі. */
export function useTaskMenuItems(task: Task, opts: { inPanel?: boolean } = {}): MenuItem[] {
  const actions = useTaskActions();
  const shell = useShell();
  const navigate = useNavigate();
  const { userId, isOwner } = useSession();
  const done = task.status === 'completed';
  const isApprover = task.assigneeId === userId || isOwner;
  const items: MenuItem[] = [];
  if (!opts.inPanel) items.push({ key: 'open', label: 'Відкрити картку', icon: <PanelRight className="size-4" />, shortcut: 'Enter', onSelect: () => shell.openTask(task.id) });
  items.push({ key: 'page', label: 'Відкрити на всю сторінку', icon: <Maximize2 className="size-4" />, onSelect: () => navigate(`/tasks/${task.id}`) });
  items.push({ type: 'separator', key: 's1' });
  if (done) items.push({ key: 'reopen', label: 'Відкрити знову…', icon: <RotateCcw className="size-4" />, onSelect: () => actions.reopen(task) });
  else if (task.subtype === 'approval' && task.approvalStatus === 'pending')
    items.push({
      key: 'decide',
      label: 'Ухвалити рішення…',
      icon: <ShieldCheck className="size-4" />,
      disabled: !isApprover,
      description: isApprover ? undefined : 'Рішення ухвалює призначений погоджувач',
      onSelect: () => actions.decide(task),
    });
  else items.push({ key: 'complete', label: 'Завершити з результатом…', icon: <Check className="size-4" />, onSelect: () => actions.complete(task) });
  if (!done) {
    items.push({ key: 'due', label: 'Перенести строк…', icon: <CalendarClock className="size-4" />, onSelect: () => actions.reschedule(task) });
    items.push({ key: 'assignee', label: 'Змінити виконавця…', icon: <UserRound className="size-4" />, onSelect: () => actions.reassign(task) });
  }
  items.push({ type: 'separator', key: 's2' });
  items.push({ key: 'link', label: 'Копіювати посилання', icon: <Link2 className="size-4" />, onSelect: () => actions.copyLink(task) });
  items.push({ key: 'archive', label: 'Архівувати…', icon: <Archive className="size-4" />, tone: 'danger', onSelect: () => actions.archive(task) });
  return items;
}

/** Кнопка «…» з меню дій задачі. */
export function TaskMenuButton({ task, inPanel, className }: { task: Task; inPanel?: boolean; className?: string }) {
  const menu = useMenu();
  const items = useTaskMenuItems(task, { inPanel });
  return (
    <>
      <IconButton label={`Дії із задачею ${task.key}`} size="sm" className={className} {...menu.triggerProps}>
        <MoreHorizontal className="size-4" aria-hidden />
      </IconButton>
      <Menu open={menu.open} onClose={menu.close} anchor={menu.anchor} items={items} label={`Дії із задачею ${task.key}`} placement="bottom-end" />
    </>
  );
}

/** Контекстне меню для рядка чи картки (права кнопка миші або Shift+F10). */
export function TaskContextMenu({ task, menu }: { task: Task; menu: ReturnType<typeof useMenu> }) {
  const items = useTaskMenuItems(task);
  return <Menu open={menu.open} onClose={menu.close} anchor={menu.anchor} items={items} label={`Дії із задачею ${task.key}`} />;
}
